import { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Alert,
  AlertTitle,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  LinearProgress,
} from '@mui/material';
import {
  Instagram,
  CheckCircle,
  TrendingUp,
  CheckCircleOutline,
  LinkOff,
  CheckBox,
  PhotoLibrary
} from '@mui/icons-material';
import { toast } from 'react-toastify';

// Import showcase components
import ShowcaseCard from '../showcase/ShowcaseCard';
import type { ShowcaseData as ShowcaseCardData } from '../showcase/ShowcaseCard';
import ShowcaseForm from '../showcase/ShowcaseForm';

// Import services
import {
  verifyInstagramConnection,
  initInstagramOAuth,
  createShowcaseBundle,
  getExistingShowcases,
  disconnectInstagram,
  updateShowcaseWithToken,
  deleteShowcaseWithToken
} from '../../services/instagramShowcaseService';

// Import mobile OAuth handlers
import {
  isMobileDevice,
  openOAuthPopup,
  monitorPopup
} from '../../utils/mobileOAuthHandler';

interface InstagramPost {
  id: string;
  caption?: string;
  media_url?: string;
  thumbnail_url?: string;
  mediaType?: string;
  [key: string]: unknown;
}

interface ExistingShowcase {
  _id: string;
  id?: string;
  media?: Array<{ key: string; mediaType: string }>;
  content?: string;
  status?: string;
  planIds?: string[];
  isFromInstagram?: boolean;
  [key: string]: unknown;
}

// Extended response type for showcase bundle creation
// Includes fields not in the shared ShowcaseBundleResponse type
interface ShowcaseBundleResponseExtended {
  success: boolean;
  message?: string;
  tempAccessToken?: string;
  showcaseIds?: string[];
  aiAnalysisCompleted?: boolean;
  isAccountClaimed?: boolean;
  canSkipClaimStep?: boolean;
  data?: Record<string, unknown>;
  [key: string]: unknown;
}

interface InstagramShowcaseFlowData {
  instagramConnected?: boolean;
  instagramToken?: string | null;
  instagramUserId?: string | null;
  instagramUsername?: string | null;
  instagramPosts?: InstagramPost[];
  instagramOnboardingToken?: string | null;
  businessId?: string | null;
  staffId?: string | null;
  isAnalyzing?: boolean;
  aiAnalysisComplete?: boolean;
  aiAnalysisFailed?: boolean;
  aiAnalysis?: unknown;
  aiAnalysisStatus?: string;
  isAccountClaimed?: boolean;
  canSkipClaimStep?: boolean;
  onboardingComplete?: boolean;
  redirectTo?: string | null;
  [key: string]: unknown;
}

interface CombinedConnectUploadStepProps {
  flowData: InstagramShowcaseFlowData;
  onUpdate: (data: Partial<InstagramShowcaseFlowData>) => void;
  onNext: () => void;
  onBack: () => void;
  onError: (message: string) => void;
  isLoading: boolean;
  setIsLoading: (loading: boolean) => void;
  setInstagramConnecting: (connecting: boolean) => void;
}

interface ConnectionData {
  success?: boolean;
  connected?: boolean;
  instagramUserId?: string;
  instagramUsername?: string;
  posts?: InstagramPost[];
  businessId?: string;
  staffId?: string;
  isAccountClaimed?: boolean;
  canSkipClaimStep?: boolean;
  onboardingComplete?: boolean;
  redirectTo?: string | null;
  [key: string]: unknown;
}

/**
 * Combined Instagram Connect & Upload Step
 *
 * This component handles:
 * 1. Instagram OAuth connection
 * 2. OAuth callback processing
 * 3. Instagram post selection
 * 4. Showcase creation from posts
 * 5. Page refresh state restoration
 *
 * === DATA FLOW ===
 *
 * Initial Connection:
 * 1. User clicks "Connect Instagram" -> handleConnectInstagram()
 * 2. Clear any existing token -> call /init-oauth API
 * 3. Backend redirects to Instagram OAuth
 * 4. User authorizes -> Instagram redirects back with code
 * 5. Backend processes -> redirects to frontend with ?instagram_token=JWT
 * 6. useEffect detects token in URL -> handleInstagramCallback()
 * 7. Store token in localStorage -> call /connection-status API
 * 8. Receive posts -> update flowData (in-memory state)
 * 9. User can select posts
 *
 * Page Refresh:
 * - Flow resets to step 0
 * - User must reconnect Instagram
 *
 * === ERROR HANDLING STRATEGY ===
 *
 * 401 Unauthorized or 404 Not Found:
 * - Token is invalid/expired or account deleted
 * - Clear localStorage.instagram_onboarding_token
 * - Reset to initial state
 * - Show "Session expired, please reconnect"
 *
 * 500 or Network Errors:
 * - KEEP localStorage token
 * - Allow user to retry without losing data
 * - Show "Connection failed, please retry"
 *
 * === STORAGE ===
 * - localStorage.instagram_onboarding_token: JWT (24h expiry)
 * - No sessionStorage - flow state not persisted
 */
const CombinedConnectUploadStep = ({
  flowData,
  onUpdate,
  onNext,
  onBack,
  onError,
  isLoading,
  setIsLoading,
  setInstagramConnecting
}: CombinedConnectUploadStepProps): React.JSX.Element => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  // Instagram connection state
  const [isVerifyingConnection, setIsVerifyingConnection] = useState<boolean>(false);
  // AI analysis status state - reserved for future use
  // const [aiAnalysisStatus, setAiAnalysisStatus] = useState<AiAnalysisStatusState>({ inProgress: false, complete: false, data: null });

  // Connection error recovery states
  const [connectionCheckError, setConnectionCheckError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  const MAX_RETRIES = 3;

  // Showcase upload state
  const [selectedInstagramPosts, setSelectedInstagramPosts] = useState<InstagramPost[]>([]);
  const [showcaseDescription, setShowcaseDescription] = useState<string>('');
  const [isInstagramDialogOpen, setIsInstagramDialogOpen] = useState<boolean>(false);

  // State for existing showcases
  const [existingShowcases, setExistingShowcases] = useState<ExistingShowcase[]>([]);
  const [isLoadingExistingShowcases, setIsLoadingExistingShowcases] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // State for edit dialog
  const [editingShowcase, setEditingShowcase] = useState<ExistingShowcase | null>(null);

  // State for delete dialog
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [showcaseToDelete, setShowcaseToDelete] = useState<ExistingShowcase | null>(null);
  const [isDeletingShowcase, setIsDeletingShowcase] = useState<boolean>(false);

  // Refs to prevent infinite loops
  const hasInitialized = useRef<boolean>(false);
  const isProcessingCallback = useRef<boolean>(false);
  const hasForceChecked = useRef<boolean>(false);

  // Fetch existing showcases for the connected staff
  const fetchExistingShowcases = useCallback(async (staffId: string): Promise<void> => {
    if (!staffId || isLoadingExistingShowcases) {
      return;
    }

    setIsLoadingExistingShowcases(true);

    try {
      const response = await getExistingShowcases(staffId);
      if (response.success) {
        setExistingShowcases((response.showcases || []).map(s => ({
          _id: (s._id as string) || s.id,
          ...s
        } as ExistingShowcase)));
      }
    } catch (error: unknown) {
      console.error('Error fetching existing showcases:', error);
      // Don't show error to user as this is non-critical
    } finally {
      setIsLoadingExistingShowcases(false);
    }
  }, [isLoadingExistingShowcases]);

  // Verify Instagram connection status
  const verifyConnection = useCallback(async (forceCheck = false): Promise<void> => {
    // Allow retry if:
    // 1. forceCheck is true (explicit retry button or forced check)
    // 2. Previous check had an error
    // 3. Not currently verifying
    const shouldSkipCheck = !forceCheck &&
                           !connectionCheckError &&
                           (hasInitialized.current || isVerifyingConnection);

    if (shouldSkipCheck) {
      return;
    }

    // Reset error state on new attempt
    if (forceCheck) {
      setConnectionCheckError(null);
      hasInitialized.current = false; // Reset for retry
    }

    // Get token from localStorage
    const token = localStorage.getItem('instagram_onboarding_token');
    if (!token) {
      hasInitialized.current = true; // Mark as initialized to prevent repeated checks
      return;
    }

    setIsVerifyingConnection(true);
    try {
      const connectionData: ConnectionData = await verifyInstagramConnection(token);

      if (connectionData.connected) {

        // Update flow data with Instagram connection info
        const updateData: Partial<InstagramShowcaseFlowData> = {
          instagramConnected: true,
          instagramToken: token, // Use the token we already have
          instagramUserId: connectionData.instagramUserId,
          instagramUsername: connectionData.instagramUsername,
          instagramPosts: connectionData.posts || [],
          // Business and staff info from new flow
          businessId: connectionData.businessId,
          staffId: connectionData.staffId,
          instagramOnboardingToken: token, // Instagram onboarding JWT (24h expiry)
          // Account claim status
          isAccountClaimed: connectionData.isAccountClaimed || false,
          canSkipClaimStep: connectionData.canSkipClaimStep || false,
          // Onboarding completion status - for Instagram sign-in redirect
          onboardingComplete: connectionData.onboardingComplete || false,
          // Backend's redirect instruction (backend-driven routing)
          redirectTo: connectionData.redirectTo || null
        };

        onUpdate(updateData);

        // Note: Success message is shown by OAuth callback handler
        // Don't show duplicate toast here for background verification

        // Fetch existing showcases if staffId is available
        if (connectionData.staffId) {
          fetchExistingShowcases(connectionData.staffId);
        }

        hasInitialized.current = true;
        hasForceChecked.current = false;
        setConnectionCheckError(null); // Clear any previous errors
        setRetryCount(0); // Reset retry count on success
      } else {
        hasInitialized.current = true; // Mark as checked even if not connected
      }
    } catch (error: unknown) {
      console.error('Error checking Instagram connection:', error);

      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string };

      // ONLY clear storage for 401/404 (invalid token or deleted account)
      // For ALL other errors (500, network, etc.) - KEEP the data for retry
      if (err.response?.status === 401 || err.response?.status === 404) {

        // Clear localStorage token
        localStorage.removeItem('instagram_onboarding_token');

        // Reset to initial state
        hasInitialized.current = false; // Allow reconnection
        setConnectionCheckError(null); // Clear error since this is expected

        // Show user-friendly message for token expiration
        if (err.response?.status === 401) {
          toast.error(
            t('instagramShowcase.setup.sessionExpired',
              'Instagram session expired. Please reconnect your account.'),
            { autoClose: 5000 }
          );
        }

        return;
      }

      // For other errors (network, 500, etc.) - KEEP the data and allow retry
      const errorMessage = err.response?.data?.message || err.message || 'Failed to verify Instagram connection';
      setConnectionCheckError(errorMessage);
      hasInitialized.current = false; // Allow retry

      // Show user-friendly error with retry option
      if (retryCount < MAX_RETRIES) {
        toast.error(
          t('instagramShowcase.setup.connectionCheckFailed',
            'Failed to verify Instagram connection. Please try again.'),
          { autoClose: 3000 }
        );
      } else {
        toast.error(
          t('instagramShowcase.setup.connectionCheckMaxRetries',
            'Unable to verify Instagram connection after multiple attempts.'),
          { autoClose: false }
        );
      }
    } finally {
      setIsVerifyingConnection(false);
    }
  }, [connectionCheckError, isVerifyingConnection, retryCount, t, onUpdate, fetchExistingShowcases]); // Add necessary dependencies

  // Handle Instagram OAuth initialization
  const handleConnectInstagram = async (): Promise<void> => {
    try {
      // Clear any existing token before starting new OAuth flow to ensure clean state
      localStorage.removeItem('instagram_onboarding_token');

      // Use the specific Instagram connecting state instead of general loading
      setInstagramConnecting(true);

      // For mobile popup flow, redirect to callback handler page
      const returnUrl = isMobileDevice()
        ? `${window.location.origin}/oauth-callback`
        : window.location.href;

      const response = await initInstagramOAuth(returnUrl);

      if (!response.authUrl) {
        throw new Error('No auth URL received from backend');
      }

      // Mobile browser: Use popup to prevent Instagram app intercept
      if (isMobileDevice()) {
        console.log('Mobile browser detected - using popup approach');

        // CRITICAL: Open popup BEFORE any async operations (iOS requirement)
        const popup = openOAuthPopup(response.authUrl);

        // Check if popup was blocked
        if (!popup || popup.closed) {
          console.warn('Popup was blocked by browser');
          setInstagramConnecting(false);

          // Fallback: Navigate to intermediate page
          navigate('/oauth-redirect', {
            state: {
              authUrl: response.authUrl,
              returnPath: location.pathname
            }
          });
          return;
        }

        console.log('OAuth popup opened successfully');

        // Monitor popup for completion
        monitorPopup(
          popup,
          async (token: string) => {
            // Success: Store token and process directly (faster than reload)
            console.log('OAuth completed successfully, token received');
            localStorage.setItem('instagram_onboarding_token', token);

            try {
              // Directly verify connection without page reload
              const connectionData: ConnectionData = await verifyInstagramConnection(token);

              if (connectionData.connected) {
                console.log('Instagram connection verified');

                // Update flow data
                onUpdate({
                  instagramConnected: true,
                  instagramToken: token,
                  instagramUserId: connectionData.instagramUserId,
                  instagramUsername: connectionData.instagramUsername,
                  instagramPosts: connectionData.posts || [],
                  businessId: connectionData.businessId,
                  staffId: connectionData.staffId,
                  instagramOnboardingToken: token,
                  isAccountClaimed: connectionData.isAccountClaimed,
                  canSkipClaimStep: connectionData.canSkipClaimStep,
                  onboardingComplete: connectionData.onboardingComplete || false,
                  isAnalyzing: true,
                  aiAnalysisComplete: false,
                  aiAnalysisFailed: false,
                  aiAnalysisStatus: 'in_progress'
                });

                // Fetch existing showcases if available
                if (connectionData.staffId) {
                  await fetchExistingShowcases(connectionData.staffId);
                }

                toast.success(t('instagramShowcase.connect.connected', 'Instagram connected successfully!'));
                setInstagramConnecting(false);
              } else {
                throw new Error('Connection verification failed');
              }
            } catch (error: unknown) {
              console.error('Error verifying connection:', error);
              setInstagramConnecting(false);
              onError(t('instagramShowcase.connect.error', 'Failed to connect Instagram. Please try again.'));
            }
          },
          (error: Error) => {
            // Error: Show message
            console.error('OAuth popup error:', error.message);
            setInstagramConnecting(false);
            onError(t('instagramShowcase.connect.error', 'Failed to connect Instagram. Please try again.'));
          }
        );

      } else {
        // Desktop browser: Use traditional redirect (works perfectly)
        console.log('Desktop browser detected - using traditional redirect');
        window.location.href = response.authUrl;
      }

    } catch (error: unknown) {
      console.error('Error initiating Instagram OAuth:', error);
      onError(t('instagramShowcase.connect.error', 'Failed to connect Instagram. Please try again.'));
      setInstagramConnecting(false);
    }
    // Note: For desktop, we don't set setInstagramConnecting(false) in finally because
    // we want the overlay to stay active during the redirect
  };

  // Handle OAuth callback (JWT token in URL)
  useEffect(() => {
    // Check both query string and hash fragment for instagram_token
    // React Router sometimes puts params in hash fragment (#_?param=value)
    const searchParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(location.hash.replace(/^#_?\?/, ''));

    const instagramToken = searchParams.get('instagram_token') || hashParams.get('instagram_token');
    const instagramError = searchParams.get('instagram_error') || hashParams.get('instagram_error');

    // Helper to clean URL params from both query string and hash
    const cleanUrlParams = (...params: string[]): void => {
      const newUrl = new URL(window.location.href);

      // Clean query params
      params.forEach(param => newUrl.searchParams.delete(param));

      // Clean hash params if they exist
      if (newUrl.hash) {
        const hashMatch = newUrl.hash.match(/^#(_\?)(.*)/);
        if (hashMatch) {
          const hashSearchParams = new URLSearchParams(hashMatch[2]);
          params.forEach(param => hashSearchParams.delete(param));

          // Reconstruct hash or remove it if empty
          const newHashParams = hashSearchParams.toString();
          newUrl.hash = newHashParams ? `#_?${newHashParams}` : '';
        }
      }

      window.history.replaceState({}, '', newUrl.toString());
    };

    // Handle error case
    if (instagramError) {
      console.error('Instagram OAuth error:', instagramError);
      onError(t('instagramShowcase.connect.error', 'Failed to connect Instagram. Please try again.'));
      cleanUrlParams('instagram_error');
      return;
    }

    // Handle success case (JWT token in URL)
    if (instagramToken) {

      // Check if this exact token is already processed AND we have the posts data
      // This prevents duplicate processing but allows re-processing if data is incomplete
      const storedToken = localStorage.getItem('instagram_onboarding_token');
      const hasPosts = flowData.instagramPosts && flowData.instagramPosts.length > 0;
      const hasRequiredData = flowData.businessId && flowData.staffId;

      if (storedToken === instagramToken && hasPosts && hasRequiredData) {
        // Clean up URL anyway
        cleanUrlParams('instagram_token');
        return;
      }

      if (storedToken === instagramToken && (!hasPosts || !hasRequiredData)) {
        // Continue to process
      }

      // Prevent multiple callback handling in progress
      if (isProcessingCallback.current) {
        return;
      }

      const handleInstagramCallback = async (): Promise<void> => {
        try {
          isProcessingCallback.current = true;
          setIsVerifyingConnection(true);
          hasInitialized.current = true;


          // Decode JWT to get basic info (client-side - no verification needed)
          const tokenParts = instagramToken.split('.');
          if (tokenParts.length !== 3) {
            throw new Error('Invalid JWT token format');
          }
          JSON.parse(atob(tokenParts[1])); // validate payload is valid JSON


          // Store token in localStorage
          localStorage.setItem('instagram_onboarding_token', instagramToken);

          // Verify Instagram connection with backend using JWT token
          const connectionData: ConnectionData = await verifyInstagramConnection(instagramToken);


          if (connectionData.connected) {

            // Update flow state with all data from backend
            const updateData: Partial<InstagramShowcaseFlowData> = {
              instagramConnected: true,
              instagramToken: instagramToken, // Use JWT token
              instagramUserId: connectionData.instagramUserId,
              instagramUsername: connectionData.instagramUsername,
              instagramPosts: connectionData.posts || [],
              // Business and staff info
              businessId: connectionData.businessId,
              staffId: connectionData.staffId,
              instagramOnboardingToken: instagramToken, // Instagram onboarding JWT (24h expiry)
              // Account claim status - trust database values, no fallbacks
              isAccountClaimed: connectionData.isAccountClaimed,
              canSkipClaimStep: connectionData.canSkipClaimStep,
              // Onboarding completion status - for Instagram sign-in redirect
              onboardingComplete: connectionData.onboardingComplete || false,
              // AI analysis starts automatically in backend after OAuth
              isAnalyzing: true,
              aiAnalysisComplete: false,
              aiAnalysisFailed: false,
              aiAnalysisStatus: 'in_progress'
            };

            onUpdate(updateData);

            // Show success message
            toast.success(t('instagramShowcase.connect.connected', 'Instagram connected successfully!'));

            // Fetch existing showcases if staffId is available
            if (connectionData.staffId) {
              fetchExistingShowcases(connectionData.staffId);
            }

            hasForceChecked.current = false;

            // Clean up URL parameter AFTER state is successfully updated
            cleanUrlParams('instagram_token', 'account_type');
          } else {
            toast.error(t('instagramShowcase.connect.error', 'Failed to connect Instagram. Please try again.'));
          }
        } catch (error: unknown) {
          console.error('OAuth callback error:', error);

          const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string };

          // ONLY clear storage for 401/404 (invalid token or deleted account)
          // For ALL other errors (500, network, etc.) - KEEP the data for retry
          if (err.response?.status === 401 || err.response?.status === 404) {

            // Clear localStorage token
            localStorage.removeItem('instagram_onboarding_token');

            // Reset flow data to force reconnection
            onUpdate({
              instagramConnected: false,
              instagramToken: null,
              instagramOnboardingToken: null,
              instagramUserId: null,
              instagramUsername: null,
              instagramPosts: []
            });

            toast.error(
              t('instagramShowcase.claim.tokenExpired',
                'Instagram session expired. Please reconnect your Instagram account.'),
              { autoClose: 5000 }
            );
          } else {
            // For other errors (network, 500, etc.) - KEEP the data for retry
            toast.error(
              t('instagramShowcase.connect.error',
                'Failed to connect Instagram. Please try again.'),
              { autoClose: 3000 }
            );
          }
        } finally {
          setIsVerifyingConnection(false);
          setInstagramConnecting(false); // Hide the loading overlay
          isProcessingCallback.current = false; // Reset processing flag
        }
      };

      handleInstagramCallback();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.hash]); // Re-run when URL changes (query or hash) to detect OAuth callback

  // Verify existing connection on mount (only once)
  // BUT skip if we have instagram_token in URL (OAuth callback will handle it)
  useEffect(() => {
    // Check both query string and hash fragment
    const searchParams = new URLSearchParams(location.search);
    const hashParams = new URLSearchParams(location.hash.replace(/^#_?\?/, ''));
    const hasInstagramToken = searchParams.has('instagram_token') || hashParams.has('instagram_token');

    if (!hasInstagramToken) {
      verifyConnection();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once on mount

  // Force connection verification if we're missing business/staff IDs but have Instagram connection
  // This runs only once when Instagram is first connected and IDs are missing
  useEffect(() => {
    if (flowData.instagramConnected &&
        (!flowData.businessId || !flowData.staffId) &&
        !isVerifyingConnection &&
        !hasForceChecked.current) {


      hasForceChecked.current = true; // Prevent multiple force checks
      verifyConnection(true); // Force verification
    }
  }, [flowData.instagramConnected, flowData.businessId, flowData.staffId, isVerifyingConnection, verifyConnection]); // Only depend on connection status and verification state

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      // Reset refs to prevent state pollution if component remounts
      hasInitialized.current = false;
      isProcessingCallback.current = false;
      hasForceChecked.current = false;
    };
  }, []);

  // Note: AI polling has been moved to parent component (InstagramShowcaseFlow.jsx)
  // This ensures polling continues even when navigating to ProfileEditStep

  // Handle Instagram post selection
  const handleInstagramPostSelect = (post: InstagramPost): void => {
    setSelectedInstagramPosts(prevSelected => {
      const isSelected = prevSelected.some(p => p.id === post.id);
      if (isSelected) {
        const newSelection = prevSelected.filter(p => p.id !== post.id);
        return newSelection;
      } else {
        // Allow multiple post selection
        const newSelection = [...prevSelected, post];
        return newSelection;
      }
    });
  };

  // Handle showcase creation or continue with existing showcases
  const handleCreateShowcase = async (): Promise<void> => {
    // If no new posts selected but have existing showcases, just proceed
    if (selectedInstagramPosts.length === 0 && existingShowcases.length > 0) {

      // Update flow data to indicate we have showcases
      onUpdate({
        showcaseIds: existingShowcases.map(s => s._id || s.id),
        isAccountClaimed: flowData.isAccountClaimed || false
      });

      // Move to next step
      onNext();
      return;
    }

    // Validate that we have at least 1 Instagram post selected if no existing showcases
    if (selectedInstagramPosts.length === 0) {
      onError(t('instagramShowcase.combined.selectPostRequired', 'Please select at least 1 Instagram post'));
      return;
    }

    if (!flowData.instagramConnected || !flowData.businessId || !flowData.staffId) {
      onError(t('instagramShowcase.upload.connectRequired', 'Please connect Instagram and ensure business setup is complete'));
      return;
    }

    setIsProcessing(true);
    setIsLoading(true);

    try {

      // Create FormData for the API call - include business/staff data to avoid session dependency
      const formData = new FormData();
      formData.append('selectedInstagramPosts', JSON.stringify(selectedInstagramPosts.map(post => post.id)));
      formData.append('showcaseDescription', showcaseDescription || 'Instagram showcase');

      // Pass business and staff info directly instead of relying on session
      formData.append('businessId', flowData.businessId!);
      formData.append('staffId', flowData.staffId!);
      formData.append('instagramOnboardingToken', flowData.instagramOnboardingToken || '');

      // Pass Instagram posts data to avoid session lookup
      formData.append('instagramPosts', JSON.stringify(flowData.instagramPosts));

      const response = await createShowcaseBundle(formData) as ShowcaseBundleResponseExtended;

      if (response.success) {

        // Update flow data with response - business/staff already exist, just add showcase info
        onUpdate({
          showcaseIds: response.showcaseIds,
          isAnalyzing: response.aiAnalysisCompleted ? false : true,
          // Account claim status from showcase bundle creation
          isAccountClaimed: response.isAccountClaimed || false,
          canSkipClaimStep: response.canSkipClaimStep || false
        });

        // Display backend message with created/skipped counts if available
        const successMessage = response?.message || t('instagramShowcase.upload.success', 'Showcase created successfully!');
        toast.success(successMessage);

        // Navigate to next step immediately
        // If AI analysis not complete, ProfileEditStep will show blocking modal
        onNext();
      } else {
        throw new Error(response.message || 'Failed to create showcase bundle');
      }
    } catch (error: unknown) {
      console.error('Error creating showcase bundle:', error);
      const err = error as { message?: string };
      onError(err.message || t('instagramShowcase.upload.error', 'Failed to create showcase. Please try again.'));
    } finally {
      setIsProcessing(false);
      setIsLoading(false);
    }
  };

  // Handle showcase delete (initiate)
  const handleDeleteShowcase = (showcase: ExistingShowcase): void => {
    console.log('Initiating delete for showcase:', showcase._id);
    setShowcaseToDelete(showcase);
    setIsDeleteDialogOpen(true);
  };

  // Handle showcase delete (confirm)
  const handleConfirmDelete = async (): Promise<void> => {
    if (!showcaseToDelete) {
      return;
    }

    setIsDeletingShowcase(true);

    try {
      console.log('Deleting showcase:', showcaseToDelete._id);

      // Call delete service with Instagram onboarding token
      const response = await deleteShowcaseWithToken(
        showcaseToDelete._id,
        flowData.instagramOnboardingToken || ''
      );

      if (response.success) {
        toast.success(t('instagramShowcase.existing.deleteSuccess', 'Showcase deleted successfully!'));

        // Remove from existing showcases list
        setExistingShowcases(prev => prev.filter(s => s._id !== showcaseToDelete._id));

        // Close dialog
        setIsDeleteDialogOpen(false);
        setShowcaseToDelete(null);
      } else {
        throw new Error(response.message || 'Failed to delete showcase');
      }
    } catch (error: unknown) {
      console.error('Error deleting showcase:', error);

      const err = error as { response?: { status?: number; data?: { message?: string } }; message?: string };

      // Handle token expiration
      if (err.response?.status === 401) {
        toast.error(
          t('instagramShowcase.claim.tokenExpired',
            'Instagram session expired. Please reconnect your Instagram account.'),
          { autoClose: 5000 }
        );
        // Clear token and reset connection
        localStorage.removeItem('instagram_onboarding_token');
        handleDisconnectInstagram();
      } else {
        toast.error(
          err.response?.data?.message ||
          err.message ||
          t('instagramShowcase.existing.deleteError', 'Failed to delete showcase. Please try again.')
        );
      }
    } finally {
      setIsDeletingShowcase(false);
    }
  };

  // Handle Instagram disconnect
  const handleDisconnectInstagram = async (): Promise<void> => {
    try {

      // Call backend to clear session data
      await disconnectInstagram();

      // Clear localStorage token
      localStorage.removeItem('instagram_onboarding_token');

      // Reset local state immediately for UI responsiveness
      setSelectedInstagramPosts([]);
      setShowcaseDescription('');
      hasInitialized.current = false;

      // Update flow data to reset Instagram connection state and business/staff info
      const resetData: Partial<InstagramShowcaseFlowData> = {
        instagramConnected: false,
        instagramToken: null,
        instagramUserId: null,
        instagramUsername: null,
        instagramPosts: [],
        aiAnalysisComplete: false,
        aiAnalysis: null,
        // Also reset business/staff info
        businessId: null,
        staffId: null,
        instagramOnboardingToken: null
      };

      onUpdate(resetData);

      setIsInstagramDialogOpen(false);
      toast.success(t('instagramShowcase.combined.disconnected', 'Instagram disconnected successfully!'));
    } catch (error: unknown) {
      console.error('Error disconnecting Instagram:', error);
      toast.error(t('instagramShowcase.combined.disconnectError', 'Failed to disconnect Instagram'));
    }
  };

  // Check if we can proceed:
  // 1. Must have Instagram connected and business/staff created
  // 2. Either have existing showcases OR have selected new posts to create
  // Note: AI analysis runs in background - blocking happens at step transition in InstagramShowcaseFlow
  const canProceed = flowData.instagramConnected &&
                     flowData.businessId &&
                     flowData.staffId &&
                     (existingShowcases.length > 0 || selectedInstagramPosts.length >= 1);

  return (
    <Box>
      {/* Header */}
      <Typography variant="h4" gutterBottom align="center" sx={{ mb: 2 }}>
        {t('instagramShowcase.combined.title', 'Connect Instagram & Create Showcase')}
      </Typography>

      <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 4 }}>
        {t('instagramShowcase.combined.subtitle', 'Connect your Instagram account and select 1 post or upload 1 file to create your showcase')}
      </Typography>

      {/* Instagram Connection Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Instagram color="primary" />
          {t('instagramShowcase.connect.title', 'Instagram Connection')}
        </Typography>

        {!flowData.instagramConnected || !flowData.businessId || !flowData.staffId ? (
          <Box>
            {isVerifyingConnection && flowData.instagramConnected ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                <CircularProgress size={20} sx={{ mr: 1, verticalAlign: 'middle' }} />
                {t('instagramShowcase.connect.verifying', 'Verifying connection...')}
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                {t('instagramShowcase.connect.description', 'Connect your Instagram account to import posts and enable AI analysis')}
              </Alert>
            )}

            {/* Connection Check Error Recovery */}
            {connectionCheckError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  retryCount < MAX_RETRIES ? (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        setRetryCount(prev => prev + 1);
                        verifyConnection(true); // Force verification
                      }}
                      disabled={isVerifyingConnection}
                    >
                      {isVerifyingConnection
                        ? t('instagramShowcase.setup.verifying', 'Verifying...')
                        : t('instagramShowcase.setup.retry', 'Retry ({{remaining}} left)', {
                            remaining: MAX_RETRIES - retryCount
                          })}
                    </Button>
                  ) : (
                    <Button
                      size="small"
                      onClick={handleDisconnectInstagram}
                    >
                      {t('instagramShowcase.setup.reconnect', 'Reconnect')}
                    </Button>
                  )
                }
              >
                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                  {t('instagramShowcase.setup.connectionError', 'Connection verification failed')}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {connectionCheckError}
                </Typography>
              </Alert>
            )}

            <Button
              variant="contained"
              startIcon={isVerifyingConnection ? <CircularProgress size={20} /> : <Instagram />}
              onClick={handleConnectInstagram}
              disabled={isLoading || isVerifyingConnection}
              size="large"
              sx={{ mt: 1 }}
            >
              {isVerifyingConnection
                ? t('instagramShowcase.connect.verifying', 'Verifying Connection...')
                : t('instagramShowcase.connect.connect', 'Connect Instagram')}
            </Button>
          </Box>
        ) : (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircle />}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {t('instagramShowcase.connect.connected', 'Instagram Connected!')}
              </Typography>
              <Typography variant="body2">
                {t('instagramShowcase.connect.connectedAs', 'Connected as: @{{username}}', {
                  username: flowData.instagramUsername
                })}
              </Typography>
              {flowData.instagramPosts && flowData.instagramPosts.length > 0 && (
                <Typography variant="body2">
                  {t('instagramShowcase.connect.postsFound', '{{count}} posts available', {
                    count: flowData.instagramPosts.length
                  })}
                </Typography>
              )}
            </Alert>

            {/* Note: AI Analysis status is displayed in the dedicated section below */}
            {/* Removed duplicate AI alerts here to avoid showing status twice */}
          </Box>
        )}
      </Paper>

      {/* Existing Showcases Section */}
      {flowData.instagramConnected && existingShowcases.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PhotoLibrary color="secondary" />
            {t('instagramShowcase.existing.title', 'Your Existing Showcases')}
            <Chip
              label={`${existingShowcases.length} ${t('instagramShowcase.existing.count', 'showcases')}`}
              size="small"
              color="secondary"
              sx={{
                backgroundColor: 'secondary.main',
                color: 'white',
                '& .MuiChip-label': {
                  color: 'white'
                }
              }}
            />
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {t('instagramShowcase.existing.description', 'These are your previously created showcases. You can continue to add new ones below.')}
            </Typography>
          </Alert>

          {isLoadingExistingShowcases ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" sx={{ ml: 1 }}>
                {t('instagramShowcase.existing.loading', 'Loading your showcases...')}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {existingShowcases.map((showcase, index) => {
                const cardShowcase: ShowcaseCardData = {
                  _id: showcase._id || `showcase-${index}`,
                  media: showcase.media || [],
                  content: showcase.content || '',
                  status: showcase.status || 'approved',
                  planIds: showcase.planIds || [],
                  isFromInstagram: showcase.isFromInstagram || false,
                };

                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={cardShowcase._id}>
                    <ShowcaseCard
                      showcase={cardShowcase}
                      onEdit={() => setEditingShowcase(showcase)}
                      onDelete={() => handleDeleteShowcase(showcase)}
                      showActions={true}
                      showStatus={true}
                      showContent={true}
                      contentMaxLength={60}
                      elevation={1}
                    />
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Paper>
      )}

      {/* Showcase Selection Section - Only show if Instagram is connected */}
      {flowData.instagramConnected && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp color="primary" />
            {t('instagramShowcase.upload.title', 'Create Your Showcase')}
            <Chip
              label={t('instagramShowcase.upload.multipleAllowed', 'Multiple Allowed')}
              size="small"
              color="primary"
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                '& .MuiChip-label': {
                  color: 'white'
                }
              }}
            />
          </Typography>

          <Alert severity={selectedInstagramPosts.length === 0 && existingShowcases.length === 0 ? "warning" : "success"} sx={{ mb: 3 }}>
            {selectedInstagramPosts.length === 0 && existingShowcases.length === 0
              ? t('instagramShowcase.combined.mustSelectOne', 'Please select at least 1 Instagram post to continue')
              : t('instagramShowcase.combined.selectPosts', 'Select Instagram posts to create your showcase')}
          </Alert>

          {/* Instagram Posts Selection */}
          {flowData.instagramPosts && flowData.instagramPosts.length > 0 && (
            <Card
              sx={{
                cursor: 'pointer',
                border: selectedInstagramPosts.length > 0 ? 2 : 1,
                borderColor: selectedInstagramPosts.length > 0 ? 'primary.main' : 'divider',
                '&:hover': { borderColor: 'primary.main' }
              }}
              onClick={() => setIsInstagramDialogOpen(true)}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Instagram color="primary" sx={{ fontSize: 48, mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('instagramShowcase.combined.instagramPosts', 'Select from Instagram Posts')}
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {t('instagramShowcase.combined.instagramPostsDesc', '{{count}} posts available from @{{username}}', {
                    count: flowData.instagramPosts.length,
                    username: flowData.instagramUsername
                  })}
                </Typography>
                {selectedInstagramPosts.length > 0 && (
                  <Chip
                    label={t('instagramShowcase.combined.selectedCount', '{{count}} Selected', { count: selectedInstagramPosts.length })}
                    color="primary"
                    size="small"
                    sx={{ mt: 1 }}
                  />
                )}
              </CardContent>
            </Card>
          )}

        </Paper>
      )}

      {/* AI Analysis Status Alerts */}
      {flowData.instagramConnected && flowData.isAnalyzing && !flowData.aiAnalysisComplete && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <AlertTitle>
            {t('instagramShowcase.upload.aiAnalyzing', 'AI is analyzing your Instagram profile...')}
          </AlertTitle>
          <Typography variant="body2">
            {t('instagramShowcase.upload.aiAnalyzingDesc',
               'Please wait while we prepare personalized suggestions for your profile. This usually takes 10-30 seconds.')}
          </Typography>
          <LinearProgress sx={{ mt: 2 }} />
        </Alert>
      )}

      {flowData.aiAnalysisFailed && (
        <Alert severity="warning" sx={{ mt: 3 }}>
          <AlertTitle>
            {t('instagramShowcase.upload.aiFailed', 'AI analysis encountered an error')}
          </AlertTitle>
          <Typography variant="body2">
            {t('instagramShowcase.upload.aiFailedDesc',
               'You can continue without AI suggestions or retry the analysis.')}
          </Typography>
          <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                // Reset AI state and trigger new analysis
                onUpdate({
                  isAnalyzing: true,
                  aiAnalysisFailed: false,
                  aiAnalysisComplete: false
                });
                // The polling useEffect will pick this up and start checking again
              }}
            >
              {t('instagramShowcase.upload.retryAI', 'Retry AI Analysis')}
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={() => onUpdate({ aiAnalysisComplete: true, aiAnalysisFailed: false })}
            >
              {t('instagramShowcase.upload.continueWithoutAI', 'Continue Without AI')}
            </Button>
          </Box>
        </Alert>
      )}

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button onClick={onBack} disabled={isLoading}>
          {t('common.back', 'Back')}
        </Button>

        <Button
          variant="contained"
          onClick={handleCreateShowcase}
          disabled={!canProceed || isLoading || isProcessing}
          startIcon={isProcessing ? <CircularProgress size={20} /> : <CheckCircleOutline />}
          size="large"
        >
          {isProcessing
            ? t('instagramShowcase.upload.creating', 'Creating Showcase...')
            : selectedInstagramPosts.length > 0
              ? t('instagramShowcase.upload.createAndContinue', 'Create Showcase & Continue')
              : t('instagramShowcase.upload.continue', 'Continue to Next Step')}
        </Button>
      </Box>


      {/* Instagram Posts Dialog */}
      <Dialog open={isInstagramDialogOpen} onClose={() => setIsInstagramDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            {t('instagramShowcase.combined.instagramPostsTitle', 'Select Instagram Post')}
            <Typography variant="body2" color="text.secondary">
              @{flowData.instagramUsername}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<LinkOff />}
            onClick={handleDisconnectInstagram}
          >
            {t('instagramShowcase.combined.disconnect', 'Disconnect')}
          </Button>
        </DialogTitle>
        <DialogContent>
          {flowData.instagramPosts && flowData.instagramPosts.length > 0 ? (
            <Grid container spacing={2}>
              {flowData.instagramPosts.map((post) => (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={post.id}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: selectedInstagramPosts.some(p => p.id === post.id) ? 2 : 1,
                      borderColor: selectedInstagramPosts.some(p => p.id === post.id) ? 'primary.main' : 'divider',
                      position: 'relative'
                    }}
                    onClick={() => handleInstagramPostSelect(post)}
                  >
                    <CardMedia
                      component="img"
                      height="200"
                      image={post.thumbnail_url || post.media_url}
                      alt="Instagram post"
                      sx={{ objectFit: 'cover' }}
                    />
                    {selectedInstagramPosts.some(p => p.id === post.id) && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                          p: 0.25,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <CheckBox sx={{ color: 'white', fontSize: 20 }} />
                      </Box>
                    )}
                    <CardContent sx={{ p: 1 }}>
                      <Typography variant="caption" noWrap>
                        {post.caption ? post.caption.substring(0, 50) + '...' : 'No caption'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 4 }}>
              {t('instagramShowcase.combined.noPostsAvailable', 'No Instagram posts available')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsInstagramDialogOpen(false)}>
            {t('instagramShowcase.combined.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={() => {
              setIsInstagramDialogOpen(false);
            }}
            variant="contained"
            disabled={selectedInstagramPosts.length === 0}
          >
            {t('instagramShowcase.combined.done', 'Done')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Showcase Dialog */}
      {editingShowcase && (
        <ShowcaseForm
          selectedStylist={flowData.staffId || ''}
          showcase={editingShowcase as { _id?: string; media?: { key: string; mediaType: string }[]; content?: string; planIds?: string[]; serviceTypeIds?: string[]; isFromInstagram?: boolean }}
          closeModal={() => {
            setEditingShowcase(null);
            // Refresh showcases after edit
            if (flowData.staffId) {
              fetchExistingShowcases(flowData.staffId);
            }
          }}
          isInDialog={true}
          hidePlans={true}
          hidePostType={true}
          onSubmitOverride={async (showcaseData) => {
            // Use Instagram onboarding token for authentication
            await updateShowcaseWithToken(
              { content: showcaseData.content, _id: showcaseData._id || '' },
              flowData.instagramOnboardingToken || ''
            );
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={isDeleteDialogOpen}
        onClose={() => !isDeletingShowcase && setIsDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('instagramShowcase.existing.deleteTitle', 'Delete Showcase?')}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('instagramShowcase.existing.deleteConfirm', 'Are you sure you want to delete this showcase? This action cannot be undone.')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsDeleteDialogOpen(false);
              setShowcaseToDelete(null);
            }}
            disabled={isDeletingShowcase}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeletingShowcase}
            startIcon={isDeletingShowcase ? <CircularProgress size={20} /> : null}
          >
            {isDeletingShowcase
              ? t('instagramShowcase.existing.deleting', 'Deleting...')
              : t('common.delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CombinedConnectUploadStep;
